/**
 * A aba Metas da Configuração (história 11.9 — FR-77, AD-27).
 *
 * Por mês, a gestão digita **três números distintos**: a meta do mês (geral), a meta da equipe e a
 * meta individual de cada vendedor. **Nenhum é soma de outro** — a tela não deriva, não rateia e
 * não impede que a soma das individuais seja diferente da meta da equipe. A soma aparece, rotulada
 * como **referência**, e quem a calcula é o serviço (sobre o que está salvo): esta tela não soma
 * nada.
 *
 * **A lista de vendedores vem do serviço, que a tira da réplica de usuário** — `perfil_base =
 * vendedor_closer` com carteira própria, ativos; nunca por nome (AD-14). Quem está sem meta aparece
 * marcado "Sem meta" para a gestão completar: zero e vazio são o mesmo estado ("sem meta
 * configurada" — decisão de 24/09/2026), e o serviço grava os dois como ausência. Quem tinha meta
 * no mês e foi desativado continua na lista daquele mês, marcado como fora da equipe (FR-65).
 *
 * **Salvar envia o mês inteiro** — o corpo é o estado completo, e a linha vazia sai do mês — **e a
 * versão lida** (`definido_em`): se outra pessoa salvou o mês depois que ele foi aberto, o serviço
 * responde `409` e a tela oferece recarregar. Pelo mesmo motivo, uma releitura que traga outra
 * versão **não** apaga o rascunho: a tela avisa que o mês mudou e deixa a pessoa decidir.
 *
 * **O mês é o calendário de `America/Sao_Paulo`**, digitado num campo de texto (`MM/AAAA`, ou
 * `AAAA-MM`): o rascunho é local, e o mês só troca quando está completo. O mês que abre é o
 * corrente no fuso de quem trabalha. Trocar de mês com rascunho não salvo pergunta antes de
 * descartar, e o campo fica travado enquanto uma gravação voa — a gravação é amarrada ao mês em
 * que foi disparada.
 *
 * **Quem não tem `configurar_metas` concedida lê, e não edita** — como o cartão da régua: esconder
 * não é autorizar (quem recusa é o serviço, com `403`), mas oferecer um campo que nunca salvaria
 * seria pior. A recusa de leitura (SDR, `403`) aparece verbatim, como toda mensagem do backend.
 */

import { type FormEvent, useEffect, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useResponsaveis } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import { DATA_E_HORA } from '@/modulos/negocios/apresentacao'
import { dinheiro } from './apresentacao'
import { type CorpoDasMetas, type MetasDoMes as Metas, useDefinirMetas, useMetas } from './api'
import './metas.css'

/** A permissão do FR-61-A que libera a digitação (só ligável em escopo `todas` — AD-18). */
export const PERMISSAO_DE_CONFIGURAR_METAS = 'configurar_metas'

const CARREGANDO = 'Carregando as metas…'
const SALVAS = 'Metas salvas.'
const VALOR_INVALIDO =
  'Informe um valor em reais, com até duas casas decimais (por exemplo, 150.000,00).'
const SEM_VENDEDOR = 'Nenhum vendedor com carteira própria ativo neste mês.'
export const MES_ALTERADO_POR_FORA =
  'As metas deste mês foram alteradas por outra pessoa enquanto você digitava.'

const ID_DO_TITULO = 'metas-titulo'
const ID_DO_MES = 'metas-mes'
const ID_DA_AJUDA_DO_MES = 'metas-mes-ajuda'
const ID_DA_RECUSA = 'metas-recusa'

/** O mês de hoje (`AAAA-MM`), no fuso de quem trabalha — nunca no do navegador (NFR-8). */
export function mesCorrente(agora: Date = new Date()): string {
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(agora)
  const ano = partes.find((parte) => parte.type === 'year')?.value ?? '1970'
  const mes = partes.find((parte) => parte.type === 'month')?.value ?? '01'
  return `${ano}-${mes}`
}

/** `AAAA-MM` → `MM/AAAA`, o jeito brasileiro de escrever o mês no campo. */
function mesParaCampo(mes: string): string {
  const [ano, numero] = mes.split('-')
  return `${numero ?? ''}/${ano ?? ''}`
}

/**
 * O mês digitado, quando completo: `MM/AAAA` (ou `AAAA-MM`) → `AAAA-MM`; incompleto é `null`.
 *
 * A digitação intermediária ("1", "10/2", "2026-") não é mês, e não troca nada: fica no rascunho
 * do campo até completar.
 */
export function lerMes(texto: string): string | null {
  const limpo = texto.trim()
  const brasileiro = /^(0[1-9]|1[0-2])\/(\d{4})$/.exec(limpo)
  if (brasileiro) return `${brasileiro[2] ?? ''}-${brasileiro[1] ?? ''}`
  const iso = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(limpo)
  return iso ? limpo : null
}

/**
 * O que a pessoa digitou, em reais, lido para o contrato: `"150.000,00"` → `"150000.00"`.
 *
 * Vazio é `null` ("sem meta"); o que não é um valor em reais com até duas casas é `undefined`, e a
 * tela recusa antes de ir à rede. Com vírgula, o ponto é separador de milhar. Sem vírgula, o ponto
 * é milhar quando separa grupos de três (`"150.000"` → `150000`) e decimal nos demais casos (quem
 * cola `150000.00` não é punido).
 */
export function lerReais(texto: string): string | null | undefined {
  const limpo = texto.replace(/R\$/g, '').replace(/\s/g, '')
  if (limpo === '') return null
  let normalizado: string
  if (limpo.includes(',')) {
    normalizado = limpo.replace(/\./g, '').replace(',', '.')
  } else if (/^\d{1,3}(\.\d{3})+$/.test(limpo)) {
    normalizado = limpo.replace(/\./g, '')
  } else {
    normalizado = limpo
  }
  return /^\d{1,12}(\.\d{1,2})?$/.test(normalizado) ? normalizado : undefined
}

const REAIS_NO_CAMPO = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** O valor do contrato (`"150000.00"`) no formato de exibição do campo (`"150.000,00"`). */
export function paraCampo(valor: string | null | undefined): string {
  if (valor === null || valor === undefined) return ''
  const numero = Number(valor)
  return Number.isFinite(numero) ? REAIS_NO_CAMPO.format(numero) : valor
}

/** Zero e vazio são o mesmo estado: "sem meta configurada" (24/09/2026). */
function estaSemMeta(texto: string): boolean {
  const lido = lerReais(texto)
  return lido === null || (lido !== undefined && Number(lido) === 0)
}

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

function eConflito(falha: unknown): boolean {
  return falha instanceof ErroDaApi && falha.status === 409
}

/** O rascunho de um mês, como os campos o mostram. */
interface Rascunho {
  doMes: string
  daEquipe: string
  individuais: Record<string, string>
}

function semear(metas: Metas): Rascunho {
  return {
    doMes: paraCampo(metas.meta_do_mes.meta),
    daEquipe: paraCampo(metas.meta_da_equipe.meta),
    individuais: Object.fromEntries(
      metas.individuais.map((linha) => [linha.usuario_id, paraCampo(linha.meta)]),
    ),
  }
}

function mesmoRascunho(a: Rascunho, b: Rascunho): boolean {
  return (
    a.doMes === b.doMes &&
    a.daEquipe === b.daEquipe &&
    Object.keys(b.individuais).every((chave) => a.individuais[chave] === b.individuais[chave])
  )
}

/** Um campo de dinheiro com o rótulo visível. */
function CampoDeReais({
  id,
  rotulo,
  valor,
  ocupado,
  recusado,
  aoTrocar,
}: {
  id: string
  rotulo: string
  valor: string
  ocupado: boolean
  recusado: boolean
  aoTrocar: (valor: string) => void
}) {
  return (
    <div className="metas__campo">
      <label className="metas__rotulo" htmlFor={id}>
        {rotulo}
      </label>
      <div className="metas__entrada-com-unidade">
        <span className="metas__unidade">R$</span>
        <input
          id={id}
          className="metas__entrada"
          type="text"
          inputMode="decimal"
          value={valor}
          disabled={ocupado}
          aria-invalid={recusado ? true : undefined}
          aria-describedby={recusado ? ID_DA_RECUSA : undefined}
          onChange={(evento) => {
            aoTrocar(evento.target.value)
          }}
        />
      </div>
    </div>
  )
}

/**
 * O formulário do mês, semeado pela versão que o pai escolheu — remontado (por `key`) quando o
 * mês ou a semente mudam, pela mesma razão do cartão da régua: re-semear rascunho por `useEffect`
 * renderiza em cascata. Ele conta ao pai se há rascunho não salvo (`aoMudarRascunho`).
 */
function FormularioDasMetas({
  metas,
  mes,
  gravacao,
  aoMudarRascunho,
}: {
  metas: Metas
  mes: string
  gravacao: ReturnType<typeof useDefinirMetas>
  aoMudarRascunho: (sujo: boolean) => void
}) {
  const [semente] = useState(() => semear(metas))
  const [rascunho, setRascunho] = useState(semente)
  const [recusados, setRecusados] = useState<ReadonlySet<string>>(new Set())
  const sujo = !mesmoRascunho(rascunho, semente)

  useEffect(() => {
    aoMudarRascunho(sujo)
  }, [sujo, aoMudarRascunho])

  function trocar(mudanca: Partial<Rascunho>) {
    setRecusados(new Set())
    gravacao.reset()
    setRascunho((atual) => ({ ...atual, ...mudanca }))
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    gravacao.reset()
    const invalidos = new Set<string>()
    const lidoDoMes = lerReais(rascunho.doMes)
    const lidoDaEquipe = lerReais(rascunho.daEquipe)
    if (lidoDoMes === undefined) invalidos.add('meta_do_mes')
    if (lidoDaEquipe === undefined) invalidos.add('meta_da_equipe')
    const linhas: CorpoDasMetas['individuais'] = []
    for (const linha of metas.individuais) {
      const lido = lerReais(rascunho.individuais[linha.usuario_id] ?? '')
      if (lido === undefined) {
        invalidos.add(linha.usuario_id)
      } else if (lido !== null) {
        linhas.push({ usuario_id: linha.usuario_id, meta: lido })
      }
    }
    setRecusados(invalidos)
    if (invalidos.size > 0) return
    gravacao.mutate({
      mes,
      corpo: {
        meta_do_mes: lidoDoMes ?? null,
        meta_da_equipe: lidoDaEquipe ?? null,
        individuais: linhas,
        versao: metas.definido_em,
      },
    })
  }

  const ocupado = gravacao.isPending

  return (
    <form className="metas__formulario" onSubmit={enviar} noValidate>
      <div className="metas__do-time">
        <CampoDeReais
          id="metas-do-mes"
          rotulo="Meta do mês"
          valor={rascunho.doMes}
          ocupado={ocupado}
          recusado={recusados.has('meta_do_mes')}
          aoTrocar={(valor) => {
            trocar({ doMes: valor })
          }}
        />
        <CampoDeReais
          id="metas-da-equipe"
          rotulo="Meta da equipe"
          valor={rascunho.daEquipe}
          ocupado={ocupado}
          recusado={recusados.has('meta_da_equipe')}
          aoTrocar={(valor) => {
            trocar({ daEquipe: valor })
          }}
        />
      </div>

      <h3 className="metas__subtitulo">Meta individual por vendedor</h3>
      {metas.individuais.length === 0 ? (
        <p className="metas__ajuda">{SEM_VENDEDOR}</p>
      ) : (
        <ul className="metas__vendedores" aria-label="Metas individuais">
          {metas.individuais.map((linha) => {
            const nome = linha.nome ?? 'Vendedor sem nome'
            const texto = rascunho.individuais[linha.usuario_id] ?? ''
            const recusado = recusados.has(linha.usuario_id)
            return (
              <li className="metas__vendedor" key={linha.usuario_id}>
                <label className="metas__nome" htmlFor={`metas-individual-${linha.usuario_id}`}>
                  {nome}
                  {linha.na_populacao ? null : (
                    <span className="metas__fora"> (fora da equipe — histórico)</span>
                  )}
                </label>
                <div className="metas__entrada-com-unidade">
                  <span className="metas__unidade">R$</span>
                  <input
                    id={`metas-individual-${linha.usuario_id}`}
                    className="metas__entrada"
                    type="text"
                    inputMode="decimal"
                    value={texto}
                    disabled={ocupado}
                    aria-invalid={recusado ? true : undefined}
                    aria-describedby={recusado ? ID_DA_RECUSA : undefined}
                    onChange={(evento) => {
                      const valor = evento.target.value
                      trocar({
                        individuais: { ...rascunho.individuais, [linha.usuario_id]: valor },
                      })
                    }}
                  />
                </div>
                {estaSemMeta(texto) ? <span className="metas__sem-meta">Sem meta</span> : null}
              </li>
            )
          })}
        </ul>
      )}

      {recusados.size > 0 ? (
        <p className="metas__erro" id={ID_DA_RECUSA} role="alert">
          {VALOR_INVALIDO}
        </p>
      ) : null}

      <div className="metas__acoes">
        <button className="metas__botao" type="submit" disabled={ocupado}>
          {ocupado ? 'Salvando…' : 'Salvar metas'}
        </button>
      </div>
    </form>
  )
}

/** O mês lido por quem não configura: os números, ou "Sem meta". */
function MetasLidas({ metas }: { metas: Metas }) {
  return (
    <div className="metas__formulario">
      <dl className="metas__lidas">
        <div>
          <dt>Meta do mês</dt>
          <dd>{metas.meta_do_mes.sem_meta ? 'Sem meta' : dinheiro(metas.meta_do_mes.meta)}</dd>
        </div>
        <div>
          <dt>Meta da equipe</dt>
          <dd>
            {metas.meta_da_equipe.sem_meta ? 'Sem meta' : dinheiro(metas.meta_da_equipe.meta)}
          </dd>
        </div>
        {metas.individuais.map((linha) => (
          <div key={linha.usuario_id}>
            <dt>{linha.nome ?? 'Vendedor'}</dt>
            <dd>{linha.sem_meta ? 'Sem meta' : dinheiro(linha.meta)}</dd>
          </div>
        ))}
      </dl>
      <p className="metas__ajuda">Só quem tem a permissão de configurar metas pode alterá-las.</p>
    </div>
  )
}

/** "Última alteração por Gabi Cardoso em 25/09/2026, 12:00" — só a gestão recebe o autor. */
function UltimaAlteracao({ metas }: { metas: Metas }) {
  const autor = metas.definido_por
  const responsaveis = useResponsaveis()
  if (metas.definido_em === null || autor === undefined || autor === null) return null
  const nome = responsaveis.data?.find((pessoa) => pessoa.id === autor)?.nome
  const quando = DATA_E_HORA.format(new Date(metas.definido_em))
  return (
    <p className="metas__ajuda">
      {nome === undefined
        ? `Última alteração em ${quando}.`
        : `Última alteração por ${nome} em ${quando}.`}
    </p>
  )
}

export function MetasDoMes() {
  const { data: sessao } = useSessao()
  const [mes, setMes] = useState(mesCorrente)
  const [mesDigitado, setMesDigitado] = useState(() => mesParaCampo(mesCorrente()))
  const [mesPendente, setMesPendente] = useState<string | null>(null)
  const [sujo, setSujo] = useState(false)
  /** Sobe a cada "Recarregar": remonta o formulário, descartando o rascunho. */
  const [geracao, setGeracao] = useState(0)
  const chave = `${mes}-01`
  const consulta = useMetas(chave)
  const gravacao = useDefinirMetas()
  const podeConfigurar =
    sessao !== undefined && sessao.permissoes[PERMISSAO_DE_CONFIGURAR_METAS] === 'concedida'
  const dados = consulta.data

  /*
   * A **semente** do formulário é a versão do mês que ele abriu. Uma releitura que traga outra
   * versão (outra pessoa salvou) só vira semente nova quando não há rascunho — ou quando ela é a
   * resposta da gravação desta própria tela. Com rascunho, a tela avisa e deixa a pessoa decidir.
   * Ajustar estado durante o render (e não num `useEffect`) é o padrão do React para derivar
   * estado de uma prop que mudou; a guarda impede o laço.
   */
  const [semente, setSemente] = useState<Metas | undefined>(undefined)
  const daPropriaGravacao =
    dados !== undefined &&
    gravacao.data !== undefined &&
    gravacao.data.mes === dados.mes &&
    gravacao.data.definido_em === dados.definido_em
  if (
    dados !== undefined &&
    dados !== semente &&
    (semente === undefined || semente.mes !== dados.mes || !sujo || daPropriaGravacao)
  ) {
    setSemente(dados)
  }
  const mudouPorFora =
    !daPropriaGravacao &&
    dados !== undefined &&
    semente !== undefined &&
    semente.mes === dados.mes &&
    dados.definido_em !== semente.definido_em
  const daGravacaoDesteMes = gravacao.variables?.mes === chave

  function pedirMes(texto: string) {
    setMesDigitado(texto)
    const lido = lerMes(texto)
    if (lido === null || lido === mes) return
    if (sujo) {
      setMesPendente(lido)
      return
    }
    abrirMes(lido)
  }

  function abrirMes(destino: string) {
    gravacao.reset()
    setSujo(false)
    setMesPendente(null)
    setMes(destino)
    setMesDigitado(mesParaCampo(destino))
  }

  function recarregar() {
    gravacao.reset()
    setSujo(false)
    setGeracao((atual) => atual + 1)
    if (dados !== undefined) setSemente(dados)
    void consulta.refetch()
  }

  return (
    <section className="metas" aria-labelledby={ID_DO_TITULO}>
      <div className="metas__cartao">
        <div className="metas__cabecalho">
          <h2 className="metas__titulo" id={ID_DO_TITULO}>
            Metas do mês
          </h2>
          <span className="metas__ajuda">
            Três números digitados e independentes: a meta do mês, a meta da equipe e a meta de cada
            vendedor. Nenhum é soma de outro.
          </span>
        </div>

        <div className="metas__corpo">
          <div className="metas__campo metas__campo--mes">
            <label className="metas__rotulo" htmlFor={ID_DO_MES}>
              Mês
            </label>
            <input
              id={ID_DO_MES}
              className="metas__entrada metas__entrada--mes"
              type="text"
              inputMode="numeric"
              placeholder="MM/AAAA"
              aria-describedby={ID_DA_AJUDA_DO_MES}
              value={mesDigitado}
              disabled={gravacao.isPending || mesPendente !== null}
              onChange={(evento) => {
                pedirMes(evento.target.value)
              }}
            />
            <span className="metas__ajuda" id={ID_DA_AJUDA_DO_MES}>
              Mês e ano, como 10/2026.
            </span>
          </div>

          {mesPendente === null ? null : (
            <div className="metas__aviso" role="alert">
              <p className="metas__aviso-texto">
                Há alterações não salvas em {mesParaCampo(mes)}. Descartar e abrir{' '}
                {mesParaCampo(mesPendente)}?
              </p>
              <div className="metas__acoes">
                <button
                  type="button"
                  className="metas__botao"
                  onClick={() => {
                    abrirMes(mesPendente)
                  }}
                >
                  Descartar e trocar de mês
                </button>
                <button
                  type="button"
                  className="metas__botao metas__botao--secundario"
                  onClick={() => {
                    setMesPendente(null)
                    setMesDigitado(mesParaCampo(mes))
                  }}
                >
                  Continuar editando
                </button>
              </div>
            </div>
          )}

          {consulta.isError ? (
            <p className="metas__erro" role="alert">
              {mensagemDe(consulta.error)}
            </p>
          ) : consulta.isLoading || semente === undefined || semente.mes !== chave ? (
            <p className="metas__ajuda" role="status">
              {CARREGANDO}
            </p>
          ) : (
            <>
              {mudouPorFora ? (
                <div className="metas__aviso" role="alert">
                  <p className="metas__aviso-texto">{MES_ALTERADO_POR_FORA}</p>
                  <button type="button" className="metas__botao" onClick={recarregar}>
                    Recarregar o mês (descarta o que foi digitado)
                  </button>
                </div>
              ) : null}
              {podeConfigurar ? (
                <FormularioDasMetas
                  key={`${semente.mes}-${semente.definido_em ?? 'nunca'}-${String(geracao)}`}
                  metas={semente}
                  mes={chave}
                  gravacao={gravacao}
                  aoMudarRascunho={setSujo}
                />
              ) : (
                <MetasLidas metas={semente} />
              )}
              {semente.soma_das_individuais === undefined ? null : (
                <p className="metas__referencia">
                  Soma das metas individuais salvas (só referência, não é a meta da equipe):{' '}
                  <strong>
                    {semente.soma_das_individuais === null
                      ? 'sem meta'
                      : dinheiro(semente.soma_das_individuais)}
                  </strong>
                </p>
              )}
              {semente.definido_por ? <UltimaAlteracao metas={semente} /> : null}
              {daGravacaoDesteMes && gravacao.isError ? (
                <div className="metas__aviso" role="alert">
                  <p className="metas__erro">{mensagemDe(gravacao.error)}</p>
                  {eConflito(gravacao.error) ? (
                    <button type="button" className="metas__botao" onClick={recarregar}>
                      Recarregar o mês (descarta o que foi digitado)
                    </button>
                  ) : null}
                </div>
              ) : null}
              {daGravacaoDesteMes && gravacao.isSuccess ? (
                <p className="metas__sucesso" role="status">
                  {SALVAS}
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </section>
  )
}
